import { Text } from "@magnetic/text";
import { type ColDef, type ITooltipParams } from "ag-grid-community";

import { TruncatedStringTooltip } from "src/components/grid/typedGridResource";
import { Constants } from "src/utils/constants";

import {
  AlternativeConditional,
  BaseColumnConfig,
  Config
} from "./baseColumnConfig";

export class StringColumn extends BaseColumnConfig<string> {
  identity(value?: string) {
    return value; // simple id generation
  }

  compare(a: string, b: string) {
    return a?.localeCompare(b);
  }

  render(data: string) {
    if (!data) {
      return Constants.EMPTY_VALUE;
    }
    return <div className="mds-text-p3">{data}</div>;
  }
}

/**
 * Follows the existing StringColumn usage, except
 * displays a dash instead when the view model has missing string for cell value.
 * Note: Doesn't check for `undefined` because there are other use cases for rendering that differently
 */
export class NullableStringColumn extends StringColumn {
  private static reuseMissingCellData: AlternativeConditional<string> = {
    check: (cellValue) => cellValue === "" || cellValue === null,
    render: () => <Text>{Constants.EMPTY_VALUE}</Text>
  };

  constructor(config: Config) {
    super(config);
    this.getConfig().alternatives = [NullableStringColumn.reuseMissingCellData];
  }
}

/**
 * Tooltip configuration for the truncated string column
 */
const tooltipRule: Pick<ColDef, "tooltipComponent" | "tooltipValueGetter"> = {
  tooltipComponent: TruncatedStringTooltip,

  tooltipValueGetter: <T extends TruncatedStringColumn, D extends string>(
    params: ITooltipParams<T, D>
  ) => {
    if (params.value === null || params.value === Constants.EMPTY_VALUE) {
      return null;
    }
    return <Text>{params.value}</Text>;
  }
};

/**
 * Some values such as MAC addresses, serial numbers, identifiers do not look good when wrapped
 */
export class TruncatedStringColumn extends StringColumn {
  constructor(config: Config) {
    config.agGridColumnDefinition ??= {};
    config.agGridColumnDefinition.autoHeight ??= false; // Keep row height to the default fixed dimension
    config.agGridColumnDefinition.wrapText ??= false; // Keep string on a single line

    // support multiple custom types, wrapped in array
    if (typeof config.agGridColumnDefinition.type === "string") {
      config.agGridColumnDefinition.type = [config.agGridColumnDefinition.type];
    }
    config.agGridColumnDefinition.type ??= []; // add custom truncate behavior ( defined in "typedGrid.tsx" )
    config.agGridColumnDefinition.type.push("truncatedMagneticText");

    config.agGridColumnDefinition.tooltipComponent ??=
      tooltipRule.tooltipComponent;

    config.agGridColumnDefinition.tooltipValueGetter ??=
      tooltipRule.tooltipValueGetter;

    super(config);
  }
}
